import React, { useState, useRef, useEffect } from 'react';

const MultiSelectDropdown = ({ label, options, selected, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (option) => {
    const newSelected = selected.includes(option)
      ? selected.filter(item => item !== option)
      : [...selected, option];
    onChange(newSelected);
  };

  const handleSelectAll = () => {
    onChange(options);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-primary-color focus:border-transparent"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm">
            {selected.length === 0 ? (
              <span className="text-gray-500">{placeholder}</span>
            ) : selected.length === 1 ? (
              selected[0]
            ) : (
              `${selected.length} selected`
            )}
          </span>
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {/* Select All / Clear All */}
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div className="flex gap-2">
              <button
                onClick={handleSelectAll}
                className="text-xs text-primary-color hover:text-primary-color-dark"
              >
                Select All
              </button>
              <span className="text-xs text-gray-400">|</span>
              <button
                onClick={handleClearAll}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Clear All
              </button>
            </div>
          </div>

          {/* Options */}
          {options.map((option) => (
            <label
              key={option}
              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => handleToggleOption(option)}
                className="mr-3 rounded border-gray-300 text-primary-color focus:ring-primary-color"
              />
              <span className="text-sm text-gray-700">{option}</span>
            </label>
          ))}

          {options.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">No options available</div>
          )}
        </div>
      )}
    </div>
  );
};

const OpenRolesFilters = ({
  searchTerm,
  selectedClients,
  selectedDepartments,
  selectedStatuses,
  filterOptions,
  onSearchChange,
  onClientChange,
  onDepartmentChange,
  onStatusChange,
  onClearFilters
}) => {
  const hasActiveFilters = searchTerm || selectedClients.length > 0 || 
                          selectedDepartments.length > 0 || selectedStatuses.length > 0;

  return (
    <div className="dashboard-card p-6 mb-6">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search Roles
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search roles and clients..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-color focus:border-transparent"
            />
          </div>
        </div>

        {/* Multi-Select Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:w-2/3">
          {/* Client Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client
            </label>
            <MultiSelectDropdown
              options={filterOptions.clients}
              selected={selectedClients}
              onChange={onClientChange}
              placeholder="All Clients"
            />
          </div>

          {/* Department Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department
            </label>
            <MultiSelectDropdown
              options={filterOptions.departments}
              selected={selectedDepartments}
              onChange={onDepartmentChange}
              placeholder="All Departments"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <MultiSelectDropdown
              options={filterOptions.statuses}
              selected={selectedStatuses}
              onChange={onStatusChange}
              placeholder="All Statuses"
            />
          </div>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="flex items-end">
            <button
              onClick={onClearFilters}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {searchTerm && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Search: "{searchTerm}"
                <button
                  onClick={() => onSearchChange('')}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            {selectedClients.map(client => (
              <span key={client} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Client: {client}
                <button
                  onClick={() => onClientChange(selectedClients.filter(c => c !== client))}
                  className="ml-2 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            ))}
            {selectedDepartments.map(dept => (
              <span key={dept} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Dept: {dept}
                <button
                  onClick={() => onDepartmentChange(selectedDepartments.filter(d => d !== dept))}
                  className="ml-2 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            ))}
            {selectedStatuses.map(status => (
              <span key={status} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Status: {status}
                <button
                  onClick={() => onStatusChange(selectedStatuses.filter(s => s !== status))}
                  className="ml-2 text-orange-600 hover:text-orange-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OpenRolesFilters;